import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import { Avatar, Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSpecialistOffersStore } from '../services';
import { cancelOffer, createOffer, getRequestDetailByIdParallel } from '../services/services';
import { Language, RequestType, Service } from '../types';

// You would need to import or create these components
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { ActionButtons } from '@features/specialistOffer/components/ActionButtons';
import { Attachments } from '@features/specialistOffer/components/Attachments';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { useTruncate } from '@hooks/useTruncate';
import PaymentIcon from '@mui/icons-material/Payment';
import { useUsersStore } from 'services/users';
import { SendOfferData, SendOfferModal } from '../components/SendOfferModal';

// Mock imports (replace with actual imports as needed)
import speak from '@assets/icons/speak.svg';

const flagCDN = 'https://flagcdn.com/w160/';

export const OfferDetailPage: React.FC = () => {
	useAppbarTitle('offerDetails');
	const { t } = useTranslation();
	const { offerId } = useParams<{ offerId: string }>();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { truncate, shouldTruncate } = useTruncate();
	const modalDimensions = useModalDimensions();
	const { user } = useUsersStore();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	const {
		selectedOffer,
		isOfferDetailLoading,
		offerDetailError,
		setSelectedOffer,
		setIsOfferDetailLoading,
		setOfferDetailError,
	} = useSpecialistOffersStore();

	const [mapOpen, setMapOpen] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);

	useEffect(() => {
		const fetchOfferDetails = async () => {
			if (!offerId) return;

			try {
				setIsOfferDetailLoading(true);
				const offerDetails = await getRequestDetailByIdParallel(offerId);
				setSelectedOffer(offerDetails);
				setIsOfferDetailLoading(false);
			} catch (error) {
				setIsOfferDetailLoading(false);
				setOfferDetailError(
					error instanceof Error ? error.message : 'Failed to fetch offer details'
				);
			}
		};

		fetchOfferDetails();

		// Cleanup function
		return () => {
			setSelectedOffer(null);
		};
	}, [offerId, setIsOfferDetailLoading, setSelectedOffer, setOfferDetailError]);

	const handleGoBack = () => {
		navigateWithLanguage('/specialist/express/offers');
	};

	const handleSendOfferClick = () => {
		setIsOfferModalOpen(true);
	};

	const handleCloseOfferModal = () => {
		setIsOfferModalOpen(false);
	};

	const handleCloseSnackbar = () => {};

	const handleSendOffer = async (offerData: SendOfferData) => {
		if (!selectedOffer || !user?.id) {
			showSnackbar(t('error'), t('missingRequiredInformationToSendOffer'), 'error');
			return;
		}

		try {
			setIsSending(true);

			await createOffer({
				farmer_id: selectedOffer.farmer.id,
				specialist_id: user.id,
				request_id: selectedOffer.id,
				price: offerData.price.toString(),
				currency: offerData.currency,
				is_free: false,
				is_default_price: false,
				consultation_type: 'EXPRESS',
				offer_status: 'ongoing',
				offer_comment: offerData.message,
			});
		} catch (error) {
			showSnackbar(t('error'), t('somethingWentWrong'), 'error');
		} finally {
			showSnackbar(t('success'), t('offerSentSuccessfully'), 'success');
			// Refresh the offer details
			const updatedOfferDetails = await getRequestDetailByIdParallel(selectedOffer.id);
			setSelectedOffer(updatedOfferDetails);

			setIsSending(false);
		}
	};

	const handleCancelOffer = async () => {
		if (!selectedOffer?.offer_id) {
			showSnackbar(t('error'), t('noOfferToCancel'), 'error');

			return;
		}

		try {
			setIsSending(true);
			await cancelOffer(selectedOffer.offer_id);
		} catch (error) {
			showSnackbar(
				t('error'),
				error instanceof Error ? error.message : t('somethingWentWrong'),
				'error'
			);
		} finally {
			showSnackbar(t('success'), t('offerCanceledDescription'), 'success');

			// Refresh the offer details or navigate back
			const updatedOfferDetails = await getRequestDetailByIdParallel(selectedOffer.id);
			setSelectedOffer(updatedOfferDetails);
			setIsSending(false);
		}
	};

	const handleNavigateToChat = () => {
		if (selectedOffer?.channelId) {
			navigateWithLanguage(`/specialist/chats?channelId=${selectedOffer.channelId}`);
		} else {
			console.log('No channel ID available for this offer');
		}
	};

	const openMapsModal = () => setMapOpen(true);
	const closeMapsModal = () => setMapOpen(false);

	if (isOfferDetailLoading) {
		return (
			<Box className="flex justify-center items-center h-96">
				<CircularProgress />
			</Box>
		);
	}

	if (offerDetailError || !selectedOffer) {
		return (
			<Box className="flex flex-col items-center justify-center h-96">
				<Typography color="error" className="mb-4">
					{offerDetailError || 'Offer not found'}
				</Typography>
				<Button variant="text" onClick={handleGoBack}>
					{t('back')}
				</Button>
			</Box>
		);
	}

	const {
		crops,
		farmer,
		services,
		total_price,
		currency,
		farm_details,
		communication_languages,
		comment,
		attachments,
		offer_id,
		request_valid_for_days,
		request_valid_for_timestamp,
		service_delivery_days,
		payment_status,
	} = selectedOffer;

	// Define values based on existing selectedOffer properties
	const description = comment;
	const is_sent = Boolean(total_price);
	const price = total_price;
	const duration_days = request_valid_for_days;
	const isPaid = payment_status === 'paid' || payment_status === 'stripe_payed';
	
	// Log payment status for debugging
	console.log(`Offer detail payment_status: ${payment_status}, isPaid: ${isPaid}`);

	return (
		<div className="flex flex-col gap-[20px] pb-5 max-md:px-5">
			{/* Back Button */}
			<div className="flex justify-between items-center mb-[15px]">
				<Button
					onClick={handleGoBack}
					variant="text"
					sx={{
						color: '#343A40',
						fontFamily: 'Roboto',
						textDecoration: 'underline',
						fontSize: '16px',
						fontStyle: 'normal',
						fontWeight: 700,
						lineHeight: 'normal',
						textTransform: 'capitalize',
						gap: '10px',
						padding: 0,
						'@media (max-width: 430px)': {
							gap: '5px',
							fontSize: '14px',
						},
					}}
				>
					<ArrowBackOutlinedIcon />
					{t('back')}
				</Button>
			</div>
			{/* Top Section with Crop, Farmer, and Consultation Info */}
			<div className="flex flex-col md:flex-row items-start md:items-center gap-0 md:gap-[35px]">
				{/* Crop Information */}
				<div className="flex items-center gap-[20px] border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					<Avatar
						src={crops[0].image_url}
						alt={crops[0].name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<div className="flex flex-col gap-[10px]">
						{shouldTruncate(crops[0].name, 20) ? (
							<Tooltip title={crops[0].name}>
								<h4 className="text-dark font-roboto text-base font-bold">
									{truncate(crops[0].name, 20)}
								</h4>
							</Tooltip>
						) : (
							<h4 className="text-dark font-roboto text-base font-bold">{crops[0].name}</h4>
						)}
					</div>
				</div>

				{/* Vertical Divider */}
				<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

				{/* Farmer Info */}
				<div className="flex items-center gap-[20px] border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					<Avatar
						src={farmer.profile_image_url}
						alt={farmer.full_name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<div className="flex flex-col gap-[5px]">
						{shouldTruncate(farmer.full_name, 20) ? (
							<Tooltip title={farmer.full_name}>
								<h4 className="text-dark font-roboto text-base font-bold">
									{truncate(farmer.full_name, 20)}
								</h4>
							</Tooltip>
						) : (
							<h4 className="text-dark font-roboto text-base font-bold">{farmer.full_name}</h4>
						)}
						{farmer.country && (
							<h6 className="text-secondary font-roboto text-base font-normal flex items-center gap-[5px]">
								<Avatar
									src={`${flagCDN}${farmer?.country?.alpha_two.toLowerCase()}.png`}
									alt={farmer?.country?.alpha_two}
									style={{ width: '20px', height: '16px', borderRadius: 0 }}
								/>
								{farmer.country.name}
							</h6>
						)}
					</div>

					<div className="flex items-center gap-5 max-md:hidden">
						{/* Vertical Divider */}
						<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

						{/* Payment Box */}
						{is_sent && price && (
							<div className="flex items-center gap-5">
								<div className="p-[10px] bg-disabled rounded-[8px]">
									<PaymentIcon sx={{ color: '#7B809A' }} />
								</div>
								<div className="flex flex-col">
									<h4 className="text-dark font-roboto text-base font-bold">€{price}</h4>
									<h6 className="text-secondary font-roboto text-base font-normal">
										{t('totalPriceText')}
									</h6>
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="hidden max-md:flex items-center gap-[20px] border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					{/* Payment Box */}
					{is_sent && price && (
						<div className="flex items-center gap-5">
							<div className="p-[10px] bg-disabled rounded-[8px]">
								<PaymentIcon sx={{ color: '#7B809A' }} />
							</div>
							<div className="flex flex-col">
								<h4 className="text-dark font-roboto text-base font-bold">€{price}</h4>
								<h6 className="text-secondary font-roboto text-base font-normal">
									{t('totalPriceText')}
								</h6>
							</div>
						</div>
					)}
				</div>

				{/* Vertical Divider */}
				<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

				<div className="flex items-center gap-[20px]">
					<div className="p-[10px] bg-disabled rounded-[8px]">
						<DateRangeOutlinedIcon sx={{ color: '#7B809A' }} />
					</div>
					<div className="flex flex-col">
						<h4 className="text-dark font-roboto text-base font-bold">
							{request_valid_for_timestamp
								? moment(request_valid_for_timestamp).format('DD.MM.YYYY')
								: service_delivery_days}
						</h4>
						<h6 className="text-secondary font-roboto text-base font-normal">
							{t('requestValidForDays', { days: selectedOffer.start_date && selectedOffer.end_date 
								? moment(selectedOffer.end_date).diff(moment(selectedOffer.start_date), 'days')
								: duration_days || 0 })}
						</h6>
					</div>
				</div>
			</div>
			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>
			<div className="flex flex-col border-b border-[#E9ECEF] pb-[20px] gap-[15px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">{t('services')}</h6>
				<div className="flex gap-[15px]">
					<div className="flex gap-[15px] flex-col md:flex-row">
						{Array.isArray(services) ? (
							services.map((service: Service, index: number) => (
								<div className="flex flex-col gap-2" key={index}>
									<div className="rounded-[10px] max-w-[380px] border border-dark flex justify-between items-center w-full">
										<p className="text-secondary font-roboto text-base font-normal p-[10px]">
											{service.name}
											{service.delivery_date && (
												<span className="block mt-1 text-sm">
													{moment(service.delivery_date).format('DD.MM.YYYY')}
												</span>
											)}
										</p>
										<div className="flex items-center h-full">
											<div className="text-dark font-roboto border-l border-dark text-base font-bold h-full bg-disabled px-[16px] py-[20px] rounded-tr-[10px] rounded-br-[10px]">
												€{service.price}
											</div>
										</div>
									</div>
									<p className="text-sm text-gray-500">
										{t('serviceDeliveryDays', { days: service.duration })}
									</p>
								</div>
							))
						) : (
							<div className="rounded-[10px] max-w-[380px] border border-dark flex justify-between items-center w-full">
								<p className="text-secondary font-roboto text-base font-normal p-[10px]">
									{services?.name}
								</p>
								<div className="flex items-center h-full">
									<div className="text-dark font-roboto border-l border-dark text-base font-bold h-full bg-disabled px-[16px] py-[20px] rounded-tr-[10px] rounded-br-[10px]">
										€{services?.price}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			{/* Farm Details Section */}
			<div className="flex flex-col border-b border-[#E9ECEF] pb-[20px] gap-[15px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">{t('farmDetails')}</h6>
				<div className="flex gap-[15px]">
					<div className="flex gap-[15px] flex-col md:flex-row">
						<div className="flex items-center gap-[15px]">
							<MapsHomeWorkOutlinedIcon sx={{ color: '#7B809A' }} />
							<p className="text-secondary font-roboto text-sm font-normal">
								{farm_details?.area} {farm_details?.unit}
							</p>
						</div>
						<div className="flex items-center gap-[15px]">
							<FmdGoodOutlinedIcon sx={{ color: '#7B809A' }} />
							<div className="flex items-center">
								<p className="text-secondary font-roboto text-sm font-normal">
									{truncate(farm_details?.location.address || '', 50)}
									<span
										className="text-primary font-roboto text-sm font-bold text-right underline decoration-solid pl-[10px] cursor-pointer"
										onClick={openMapsModal}
									>
										{t('viewOnMap')}
									</span>
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Communication Languages Section */}
			<div className="flex flex-col gap-[10px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">
					{t('communicationLanguage')}
				</h6>
				<div className="flex gap-[10px] items-center">
					<img className="text-secondary" src={speak} alt="speak" />
					<div className="flex gap-[10px] items-center">
						{communication_languages?.map((language: Language) => (
							<Chip
								key={language.id}
								label={language.name}
								variant="outlined"
								sx={{
									width: 'max-content',
									borderRadius: '8px',
									border: '1px solid #F8F9FA',
									backgroundColor: '#F8F9FA',
									padding: '1px 10px',
									color: '#7B809A',
									fontFamily: 'Roboto',
									fontSize: '16px',
									fontStyle: 'normal',
									fontWeight: 400,
									lineHeight: '140%',
									height: '24px',
									textTransform: 'camelcase',
									'& .MuiChip-label': {
										padding: 0,
									},
								}}
							/>
						))}
					</div>
				</div>
			</div>
			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>
			{/* Description Section */}
			<div className="flex flex-col gap-[10px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">{t('info')}</h6>
				<div
					className="text-secondary roboto-regular text-[16px] leading-normal flex flex-col gap-[10px] w-full"
					dangerouslySetInnerHTML={{ __html: description || '' }}
				></div>
			</div>
			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>
			{attachments && attachments.length > 0 && <Attachments files={attachments} />}
			{/* Action Buttons */}
			<ActionButtons
				onBack={handleGoBack}
				onChat={handleNavigateToChat}
				onSendOffer={handleSendOfferClick}
				hasOffer={!!offer_id}
				requestType={RequestType.EXPRESS}
				hideActions={false}
				isPaid={isPaid}
			/>
			{/* Maps Modal */}
			<GeneralMuiModal
				maxWidth="xl"
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={closeMapsModal}
				content={
					<TaMaps
						disabled={true}
						marker={{
							lng: Number(farm_details?.location.longitude || 0),
							lat: Number(farm_details?.location.latitude || 0),
						}}
						currentAddress={farm_details?.location.address || ''}
					/>
				}
			/>
			{/* Send Offer Modal */}
			<SendOfferModal
				isOpen={isOfferModalOpen}
				onClose={handleCloseOfferModal}
				onSendOffer={handleSendOffer}
				onCancelOffer={handleCancelOffer}
				onChat={handleNavigateToChat}
				currency={currency || 'EUR'}
				initialPrice={price || 0}
				isEdit={!!offer_id}
				requestId={selectedOffer.id}
				farmerId={selectedOffer.farmer.id}
				specialistId={user?.id}
			/>
		</div>
	);
};
